/**
 * Cross-tab signout coordination.
 *
 * When the user signs out (or is signed out by an auth failure) in one
 * tab, every other monitor tab on the same origin should tear down its
 * session too. A BroadcastChannel keyed on 'monitor-auth' fans a small
 * `{ type: 'signout' }` message out to peer tabs; each subscribing tab
 * runs its cleanup handler when it arrives.
 *
 * BroadcastChannel is not available in every environment (older Safari,
 * some private-mode configurations, jsdom without the shim). Feature
 * detection makes both publish and subscribe inert when the API is
 * missing, so the single-tab flow keeps working unchanged.
 *
 * Loop guard: a tab that receives a remote signout will clear its own
 * token, which can cause an in-flight Apollo query to 401, which fires
 * the auth-failure handler, which would post a fresh signout. Left
 * unchecked that fans back out to every peer (which then repeats the
 * dance). `subscribeToSignout` sets a module-level suppression flag
 * around the handler; `publishSignout` respects the flag and drops any
 * post issued while it is set. Handlers are still free to call
 * `publishSignout`, so the code stays free of "am I originating?"
 * plumbing at every call site.
 */

const CHANNEL_NAME = "monitor-auth";
const SIGNOUT_MESSAGE = { type: "signout" };

let channel = null;
let suppressed = false;

function getChannel() {
  if (channel) return channel;
  if (typeof window === "undefined") return null;
  if (typeof window.BroadcastChannel !== "function") return null;
  try {
    channel = new window.BroadcastChannel(CHANNEL_NAME);
  } catch {
    // Some hardened browser modes throw from the constructor even
    // though BroadcastChannel is present. Treat that as "unavailable".
    channel = null;
  }
  return channel;
}

export function publishSignout() {
  if (suppressed) return;
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage(SIGNOUT_MESSAGE);
  } catch {
    // postMessage can throw if the channel has been closed (page
    // unloading, tab suspended). Nothing useful to do here.
  }
}

export function subscribeToSignout(handler) {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event) => {
    if (!event || !event.data || event.data.type !== "signout") return;
    suppressed = true;
    try {
      handler();
    } finally {
      suppressed = false;
    }
  };
  ch.addEventListener("message", listener);
  return () => {
    try {
      ch.removeEventListener("message", listener);
    } catch {
      // Channel already closed. Nothing to detach from.
    }
  };
}

// Test hook: force the singleton to be recreated between tests so a
// stale channel from a previous test doesn't leak listeners into the
// next one. Not part of the public surface.
export function __resetTabsForTests() {
  if (channel) {
    try {
      channel.close();
    } catch {
      // Already closed.
    }
  }
  channel = null;
  suppressed = false;
}
